import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface UnsavedChangesDialogProps {
  open: boolean;
  onDiscard: () => void;
  onCancel: () => void;
  /** When provided, shows a 3-button layout: Go Back / Discard / Save */
  onSave?: () => void;
}

/**
 * Reusable dialog for unsaved changes.
 *
 * 2-button mode (default): "Keep editing" / "Discard changes"
 * 3-button mode (when onSave provided): "Go Back" / "Discard" / "Save"
 */
export function UnsavedChangesDialog({
  open,
  onDiscard,
  onCancel,
  onSave,
}: UnsavedChangesDialogProps): React.ReactElement {
  if (onSave) {
    // 3-button mode: Go Back / Discard / Save
    return (
      <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Go Back
            </Button>
            <Button variant="outline" size="sm" onClick={onDiscard}>
              Discard
            </Button>
            <Button variant="default" size="sm" onClick={onSave}>
              Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // 2-button mode (backward compatible)
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes that will be lost if you leave this page.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={onDiscard}>Discard changes</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
